import { FC, useEffect, useState } from "react";
import { Output } from "valibot";

import { Message, MessageType } from "components/Bubble/Message";
import {
  IntegrationFormFieldsSchema,
  IntegrationInfoSchema,
} from "components/SonicBot/schemas";
import { LocaleSchema } from "shared/schemas";
import { isValidEmail } from "shared/utils/isValidEmail";

interface AgentHandoffFormFieldsProps {
  integration: Output<typeof IntegrationInfoSchema>;
  generationLanguage: Output<typeof LocaleSchema>;
  handleSendIntegrationData: ({
    configType,
    integrationData,
  }: {
    configType: string;
    integrationData: any;
  }) => Promise<void>;
}

const AgentHandoffFormFields: FC<AgentHandoffFormFieldsProps> = ({
  integration,
  generationLanguage,
  handleSendIntegrationData,
}) => {
  const [currentEditingIndex, setCurrentEditingIndex] = useState<number>(-1);
  const [formFields, setUserFields] = useState(
    integration.required_user_fields || [],
  );

  useEffect(() => {
    if (currentEditingIndex === -1) {
      const increaseIndexTimeout = setTimeout(() => {
        setCurrentEditingIndex(0);
      }, 500);

      return () => clearTimeout(increaseIndexTimeout);
    }
  }, [currentEditingIndex]);

  const increaseCurrentIndex = () => {
    setCurrentEditingIndex(current => current + 1);
  };

  const handleOnMessageSubmit = async (id: string, value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue === "") {
      return new Error(
        `${id.charAt(0).toUpperCase() + id.slice(1)} cannot be empty`,
      );
    }

    if (id === "name" && trimmedValue.length <= 3) {
      return new Error("Name must be more than 3 letters");
    }

    if (id === "email" && !isValidEmail(trimmedValue)) {
      return new Error("Invalid email address");
    }

    const updatedUserFields = formFields.map(field =>
      field.name === id ? { ...field, value: trimmedValue } : field,
    );

    setUserFields(updatedUserFields);

    if (id === "email") {
      await handleOnLastMessageSubmit(updatedUserFields);
    }
    increaseCurrentIndex();
  };

  const handleOnLastMessageSubmit = async (
    formFields: Output<typeof IntegrationFormFieldsSchema>[],
  ) => {
    handleSendIntegrationData({
      configType: integration.config_type,
      integrationData: {
        form_fields: formFields,
      },
    });
  };

  return (
    <div className="mt-2 flex flex-col gap-3" id="zendeskIntegrationDiv">
      {formFields.map(
        (field, idx) =>
          idx <= currentEditingIndex && (
            <Message
              key={field.name}
              generationLanguage={generationLanguage}
              id={field.name}
              isNonEditable={idx !== currentEditingIndex}
              onSubmit={handleOnMessageSubmit}
              type={field.type as MessageType}
              value=""
              placeholder={field.placeholder}
              label={field.label}
              isRequired={field.required}
            />
          ),
      )}
    </div>
  );
};

export default AgentHandoffFormFields;
